import { createContext, useState, useEffect } from "react";
import i18n from "../i18n";
import { clearAuthToken, getAuthToken, getStoredAuthData, setStoredAuthData, parseJwt } from "../util/auth";
import { getApiChara } from "../util/getApi";

const AuthContext = createContext({
    isLoggedIn: false,
    token: null,
    userName: "",
    userId: "",
    role: "",
    tenantOrClientId: "",
    expiration: null,
    hospitalInfo: {},
    isLoadingHospitalData: false,
    hospitalData: {
        hospitalName: "",
        hospitalAddress: "",
        regnumber: "",
        doctorName: "",
        hospitalInstructions: "",
        PrescHeader: false,
        PrescFooter: false,
        prescriptionLanguage: "english",
        hospitalDefaultBill: "",
        CheckUpSaveBill: false,
        CheckUpSavePrescription: false,
        OPDPrintPrescription: false,
        PrintCertificate: false,
        PrintBill: false,
        OPDPrintBill: false,
        IPDPrintBill: false,
        boolIpdAccess: false,
        boolOPDVoiceCommand: false,
        ipdSavePrescription: false,
        ipdPrintPrescription: false,
        selectPrescription: "1",
        selectHeader: "1",
        applicationThemeColour: "TEAL",
        applicationLanguage: "ENGLISH",
        PrintCasePaper: false,
        selectSpecialities: "",
        headerBgColor: "#f0fdfa",
    },
    onLogin: (token, userName, userid, role, tenantOrClientId) => { },
    onLogout: () => { },
    setHospitalData: (hospitalInfo, configMap) => { },
    setTheme: (color) => { },
    fetchHospitalData: async () => { },
});

export const AuthContextProvider = (props) => {
    // Auth State
    const storedData = getStoredAuthData();
    const [token, setToken] = useState(storedData.token || null);
    const [userName, setUserName] = useState(storedData.userName || "");
    const [userId, setUserId] = useState(storedData.userId || "");
    const [role, setRole] = useState(storedData.role || "");
    const [tenantOrClientId, setTenantOrClientId] = useState(storedData.tenantOrClientId || "");
    const [expiration, setExpiration] = useState(storedData.expiration || null);

    // Hospital & Config State - Initialized to defaults, will be populated by fetchHospitalData
    const [hospitalName, setHospitalName] = useState("");
    const [hospitalAddress, setHospitalAddress] = useState("");
    const [regnumber, setRegnumber] = useState("");
    const [hospitalDefaultBill, setHospitalDefaultBill] = useState("");
    const [hospitalInstructions, setHospitalInstructions] = useState("");

    const [CheckUpSaveBill, setCheckUpSaveBill] = useState(false);
    const [CheckUpSavePrescription, setCheckUpSavePrescription] = useState(false);
    const [OPDPrintPrescription, setOPDPrintPrescription] = useState(false);
    const [PrintCertificate, setPrintCertificate] = useState(false);
    const [PrintBill, setPrintBill] = useState(false);
    const [OPDPrintBill, setOPDPrintBill] = useState(false);
    const [IPDPrintBill, setIPDPrintBill] = useState(false);
    const [PrescHeader, setPrescHeader] = useState(false);
    const [PrescFooter, setPrescFooter] = useState(false);
    const [boolIpdAccess, setBoolIpdAccess] = useState(false);
    const [boolOPDVoiceCommand, setBoolOPDVoiceCommand] = useState(false);
    const [ipdSavePrescription, setIpdSavePrescription] = useState(false);
    const [ipdPrintPrescription, setIpdPrintPrescription] = useState(false);

    const [selectPrescription, setSelectPrescription] = useState("1");
    const [selectHeader, setSelectHeader] = useState("1");
    const [prescriptionLanguage, setPrescriptionLanguage] = useState("english");

    const [doctorName, setDoctorName] = useState("");
    const [prescriptionHeader, setPrescriptionHeader] = useState("");
    const [applicationThemeColour, setApplicationThemeColour] = useState(localStorage.getItem('appTheme') || "TEAL");
    const [applicationLanguage, setApplicationLanguage] = useState("ENGLISH");
    const [PrintCasePaper, setPrintCasePaper] = useState(false);
    const [selectSpecialities, setSelectSpecialities] = useState("");
    const [headerBgColor, setHeaderBgColor] = useState("#f0fdfa");

    const [hospitalInfo, setHospitalInfo] = useState({});
    const [isLoadingHospitalData, setIsLoadingHospitalData] = useState(!!storedData.token);

    const isLoggedIn = !!token;

    useEffect(() => {
        // Optional: Auto-logout if token expired
        if (token && expiration) {
            const remainingTime = new Date(expiration).getTime() - new Date().getTime();
            if (remainingTime <= 0) {
                logoutHandler();
            } else {
                fetchHospitalData();
            }
        }
    }, [token, expiration]);

    // Handle Application Language Change
    useEffect(() => {
        if (applicationLanguage) {
            i18n.changeLanguage(applicationLanguage);
        }
    }, [applicationLanguage]);

    const setTheme = (color) => {
        setApplicationThemeColour(color);
        localStorage.setItem('appTheme', color);
    };

    const loginHandler = (token, userName, userid, role, tenantOrClientId) => {
        setToken(token);
        setUserName(userName);
        setUserId(userid);
        setRole(role);
        setTenantOrClientId(tenantOrClientId);

        let expirationDate = new Date();
        const decodedToken = parseJwt(token);
        if (decodedToken && decodedToken.exp) {
            expirationDate = new Date(decodedToken.exp * 1000);
        } else {
            expirationDate.setDate(expirationDate.getDate() + 30);
        }

        setExpiration(expirationDate.toISOString());

        setStoredAuthData({
            token,
            userName,
            userId: userid,
            role,
            tenantOrClientId,
            expiration: expirationDate.toISOString()
        });
    };

    const logoutHandler = () => {
        setToken(null);
        setUserName("");
        setUserId("");
        setRole("");
        setTenantOrClientId("");
        setExpiration(null);
        setHospitalName("");
        setHospitalAddress("");
        setRegnumber("");
        clearAuthToken();
    };

    const setHospitalDataHandler = (hospitalInfo, configMap) => {
        console.log("Setting Hospital Data with Config:", configMap);
        const hName = hospitalInfo?.name || "";
        const hAddress = `${hospitalInfo?.address || ""}, ${hospitalInfo?.city || ""} - ${hospitalInfo?.phoneNumber || ""}${hospitalInfo?.altPhoneNumber ? ", " + hospitalInfo.altPhoneNumber : ""}`;
        const hReg = hospitalInfo?.registrationNumber || "";

        setHospitalName(hName);
        setHospitalAddress(hAddress);
        setRegnumber(hReg);

        setHospitalDefaultBill(configMap["opdFee"] || "");
        setHospitalInstructions(configMap["prescriptionFooter"] || "");

        setCheckUpSaveBill(configMap["boolCheckUpSaveBill"] === "true");
        setCheckUpSavePrescription(
            configMap["boolCheckUpSavePrescription"] === "true" ||
            configMap["CheckUpSavePrescription"] === "true" ||
            configMap["checkupSavePrescription"] === "true"
        );
        setOPDPrintPrescription(
            configMap["boolOPDPrintPrescription"] === "true" ||
            configMap["boolOpdPrintPrescription"] === "true" ||
            configMap["OPDPrintPrescription"] === "true" ||
            configMap["opdPrintPrescription"] === "true"
        );
        setPrintCertificate(configMap["boolPrintCert"] === "true");
        setPrintBill(configMap["boolPrintBill"] === "true");
        setOPDPrintBill(configMap["boolOpdPrintBill"] === "true" || configMap["boolOPDPrintBill"] === "true" || configMap["boolPrintBill"] === "true");
        setIPDPrintBill(configMap["boolIpdPrintBill"] === "true" || configMap["boolIPDPrintBill"] === "true");
        setPrescHeader(configMap["boolPrescHeader"] === "true");
        setPrescFooter(configMap["boolPrescFooter"] === "true");

        // Use exact keys from API response
        setBoolIpdAccess(configMap["boolIPDAccess"] === "true" || configMap["boolIpdAccess"] === "true");
        setBoolOPDVoiceCommand(configMap["boolOPDVoiceCommand"] === "true");
        setIpdSavePrescription(configMap["boolIPDSavePrescription"] === "true" || configMap["boolIpdSavePrescription"] === "true");
        setIpdPrintPrescription(configMap["boolIPDPrintPrescription"] === "true" || configMap["boolIpdPrintPrescription"] === "true");

        setSelectPrescription(configMap["selectPrescription"] || "1");
        setSelectHeader(configMap["selectHeader"] || configMap["selectHeaderTemplate"] || "1");

        const themeColor = configMap["applicationThemeColour"] || "TEAL";
        setTheme(themeColor);

        setApplicationLanguage(configMap["applicationLanguage"] || "ENGLISH");
        setPrintCasePaper(configMap["boolPrintCasePaper"] === "true");
        setSelectSpecialities(configMap["selectSpecialities"] || "");

        if (configMap["selectLanguage"]) {
            setPrescriptionLanguage(configMap["selectLanguage"].toLowerCase());
        } else if (configMap["prescriptionLanguage"]) {
            setPrescriptionLanguage(configMap["prescriptionLanguage"].toLowerCase());
        }

        // Extract Doctor Name from Prescription Header if present
        if (configMap["prescriptionHeader"]) {
            setPrescriptionHeader(configMap["prescriptionHeader"]);
            const lines = configMap["prescriptionHeader"].split("\n");
            if (lines.length > 1) {
                setDoctorName(lines[1].trim());
            } else {
                setDoctorName(lines[0].trim());
            }
        }
        setHeaderBgColor(configMap["headerBgColor"] || "#f0fdfa");
    };


    const fetchHospitalData = async () => {
        const token = getAuthToken();
        if (!token || token === "EXPIRED") return;

        setIsLoadingHospitalData(true);
        try {
            const apiChar = getApiChara();
            const response = await fetch(`${apiChar}/HospitalConfiguration/getHospitalInfoWithAllConfigDetails`, {
                headers: {
                    Authorization: "Bearer " + token
                },
            });

            if (response.ok) {
                const data = await response.json();
                const configMap = {};
                if (data.configurations) {
                    data.configurations.forEach(config => {
                        configMap[config.paramCode] = config.paramValue;
                        const pName = (config.paramName || "").toLowerCase().trim();
                        const pCode = (config.paramCode || "").toLowerCase().trim();

                        // IPD Access
                        if (pName === "ipd access" || pCode === "boolipdaccess") {
                            configMap["boolIPDAccess"] = config.paramValue;
                        }
                        // OPD Print Prescription
                        if (pName === "opd print prescription" || pName === "print prescription" || pCode === "boolopdprintprescription") {
                            configMap["boolOpdPrintPrescription"] = config.paramValue;
                        }
                        // CheckUp Save Prescription
                        if (pName === "checkup save prescription" || pName === "save prescription" || pCode === "boolcheckupsaveprescription") {
                            configMap["boolCheckUpSavePrescription"] = config.paramValue;
                        }
                        // IPD Print Prescription
                        if (pName === "ipd print prescription" || pCode === "boolipdprintprescription") {
                            configMap["boolIPDPrintPrescription"] = config.paramValue;
                        }
                        // OPD Print Bill
                        if (pName === "opd print bill" || pCode === "boolopdprintbill") {
                            configMap["boolOpdPrintBill"] = config.paramValue;
                        }
                        // IPD Print Bill
                        if (pName === "ipd print bill" || pCode === "boolipdprintbill") {
                            configMap["boolIpdPrintBill"] = config.paramValue;
                        }
                        // IPD Save Prescription
                        if (pName === "ipd save prescription" || pCode === "boolipdsaveprescription") {
                            configMap["boolIPDSavePrescription"] = config.paramValue;
                        }
                        // Print Case Paper
                        if (pName === "print case paper" || pCode === "boolprintcasepaper") {
                            configMap["boolPrintCasePaper"] = config.paramValue;
                        }
                        // OPD Voice Command
                        if (pName === "opd voice command" || pCode === "boolopdvoicecommand") {
                            configMap["boolOPDVoiceCommand"] = config.paramValue;
                        }
                        // Select Specialities
                        if (pName === "select specialities" || pCode === "selectspecialities") {
                            configMap["selectSpecialities"] = config.paramValue;
                        }
                        // OPD Fee
                        if (pName === "opd fee") {
                            configMap["opdFee"] = config.paramValue;
                        }
                        // Header BG Color
                        if (pName === "header background colour" || pCode === "headerbgcolor") {
                            configMap["headerBgColor"] = config.paramValue;
                        }
                    });
                }
                const hInfo = data.hospitalInfo || {};
                setHospitalInfo(hInfo);
                setHospitalDataHandler(hInfo, configMap);
            }
        } catch (error) {
            console.error("Error fetching hospital data in context:", error);
        } finally {
            setIsLoadingHospitalData(false);
        }
    };



    const contextValue = {
        isLoggedIn,
        token,
        userName,
        userId,
        role,
        tenantOrClientId,
        expiration,
        hospitalInfo,
        isLoadingHospitalData,
        hospitalData: {
            hospitalName,
            hospitalAddress,
            regnumber,
            doctorName,
            prescriptionHeader,
            hospitalInstructions,
            PrescHeader,
            PrescFooter,
            prescriptionLanguage,
            hospitalDefaultBill,
            CheckUpSaveBill,
            CheckUpSavePrescription,
            OPDPrintPrescription,
            PrintCertificate,
            PrintBill,
            OPDPrintBill,
            IPDPrintBill,
            boolIpdAccess,
            boolOPDVoiceCommand,
            ipdSavePrescription,
            ipdPrintPrescription,
            selectPrescription,
            selectHeader,
            applicationThemeColour,
            applicationLanguage,
            PrintCasePaper,
            selectSpecialities,
            headerBgColor
        },
        onLogin: loginHandler,
        onLogout: logoutHandler,
        setHospitalData: setHospitalDataHandler,
        setTheme,
        fetchHospitalData
    };


    return (
        <AuthContext.Provider value={contextValue}>
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthContext;